// Mock-only scenario launcher.
//
// Every gate in the flow is reachable from a URL, but nobody remembers which id
// or which scenario produces which wall — so the two entry pages list them. It
// renders only when the mock API is active (isMockApi() is false in every
// non-mock env), so it can never reach a real build.
//
// Shared by the boost page and the casting-calls page: one list, so a scenario
// added for a demo cannot exist on one entry point and not the other.

import React from "react";
import styled from "styled-components";
import { MOCK_IDS } from "../../../shared/api/submission";

export interface IScenario {
  group: string;
  label: string;
  href: string;
  note?: string;
}

export const buildScenarios = (): IScenario[] => {
  const open = `bd=${MOCK_IDS.openBreakdown}&roleid=${MOCK_IDS.openRole}`;
  const cross = `bd=${MOCK_IDS.crossBorderBreakdown}&roleid=${MOCK_IDS.crossBorderRole}`;
  const calls = "/submission/casting-calls";
  // Step 4 needs a verified account, a saved draft and a verified phone, so a
  // link straight to it bounces to email verification. These rows go through
  // the seed page, which walks steps 1-3 against the mock API and lands on the
  // target — otherwise every row below costs three steps and two codes before
  // the thing being demonstrated is even on screen.
  const seed = (to: string, extra?: string) =>
    `/submission/mock-seed?to=${encodeURIComponent(to)}&${cross}${
      extra ? `&${extra}` : ""
    }`;
  const review = (extra?: string) => seed("/submission/flow/step/4", extra);
  return [
    { group: "CA vs US walkthrough", label: "Run it as Canada — trial offered",
      href: `/submission/boost?${cross}&ctry=CA`,
      note: "Streaming Series - Northern Line, casts in Vancouver AND Los Angeles" },
    { group: "CA vs US walkthrough", label: "Run the SAME job as the US — no trial, submits free",
      href: `/submission/boost?${cross}&ctry=US`,
      note: "no country gate either way: the job casts in both" },

    { group: "Browse (casting calls)", label: "Canada — Vancouver, Victoria, Montreal",
      href: `${calls}?ctry=CA`,
      note: "8 calls: happy path, closes today, cross-border, all-cast, closed, expired, accented title" },
    { group: "Browse (casting calls)", label: "United States — Los Angeles + New York",
      href: `${calls}?ctry=US`,
      note: "the cross-border job appears here too, matched on its US city" },
    { group: "Browse (casting calls)", label: "Mexico — Mexico City + Guadalajara",
      href: `${calls}?ctry=MX`,
      note: "where the country gate's exit lands" },
    { group: "Browse (casting calls)", label: "One city only (the gate's exit link)",
      href: `${calls}?countryCode=MX&cityId=5678`,
      note: "Mexico City only — Guadalajara drops out" },
    { group: "Browse (casting calls)", label: "A market with nothing in it",
      href: `${calls}?ctry=GB`,
      note: "empty state, from a real filter rather than a scenario flag" },

    { group: "Happy path", label: "Open breakdown, closes in 12 days",
      href: `/submission/boost?${open}` },
    { group: "Happy path", label: "No role in the link (pick one here)",
      href: `/submission/boost?bd=${MOCK_IDS.openBreakdown}` },
    { group: "Happy path", label: "Browse instead (no bd at all)",
      href: calls },

    { group: "Closed / expired", label: "Breakdown closed",
      href: `/submission/boost?bd=${MOCK_IDS.closedBreakdown}`,
      note: "gated on this page now" },
    { group: "Closed / expired", label: "Role already cast",
      href: `/submission/boost?bd=${MOCK_IDS.roleCastBreakdown}&roleid=${MOCK_IDS.castRole}` },
    { group: "Closed / expired", label: "Already submitted to this casting call",
      href: seed("/submission/flow/step/2", "mockScenario=alreadySubmitted"),
      note: "an account with a submission to any role here: the flow stops" },
    { group: "Closed / expired", label: "Breakdown does not exist (404)",
      href: `/submission/boost?bd=${MOCK_IDS.notFoundBreakdown}` },
    { group: "Closed / expired", label: "Closes today (hours, then minutes)",
      href: `/submission/boost?bd=${MOCK_IDS.closingTodayBreakdown}&roleid=${MOCK_IDS.openRole}` },
    { group: "Closed / expired", label: "Force any breakdown closed",
      href: `/submission/boost?${open}&mockScenario=expired` },
    { group: "Closed / expired", label: "Expired discovered on step 1",
      href: `/submission/flow/step/1?${open}&mockScenario=expired` },
    { group: "Closed / expired", label: "Expired, status still Open",
      href: `/submission/boost?bd=${MOCK_IDS.staleDeadlineBreakdown}&roleid=${MOCK_IDS.staleDeadlineRole}`,
      note: "deadline was yesterday; the gate reads the clock (E8), so this lands on the closed page" },
    { group: "Closed / expired", label: "Closed and all-roles-cast, side by side",
      href: `${calls}?ctry=CA`,
      note: "Northbound reads Closed, Harbour Line reads All roles cast — different states, different copy" },

    { group: "Country gate", label: "Visitor in Mexico, breakdown in Vancouver",
      href: `/submission/boost?${open}&ctry=MX`, note: "sheet appears on step 2" },
    { group: "Country gate", label: "Visitor in the US (also flips the trial copy)",
      href: `/submission/boost?${open}&ctry=US` },
    { group: "Country gate", label: "Same country, different city — must NOT gate",
      href: `/submission/boost?bd=0175452&roleid=505401&ctry=CA`,
      note: "Victoria job, Vancouver visitor: welcome, no sheet" },
    { group: "Country gate", label: "Geo lookup unavailable",
      href: `/submission/boost?${open}&mockScenario=geoFail` },

    { group: "Step 4 - the A/B split", label: "Variant A - review first, then the trial offer",
      href: review("step4=a"),
      note: "Figma's path: confirm the submission, then pay" },
    { group: "Step 4 - the A/B split", label: "Variant B - paywall first, then review",
      href: review("step4=b"),
      note: "step 4 sends an actor who owes a plan straight on to the paywall" },
    { group: "Step 4 - the A/B split", label: "Let the sticky 50/50 decide (no ?step4=)",
      href: review(),
      note: 'clear it first: localStorage.removeItem("ab-test-submission-step4")' },

    { group: "Step 4 - who pays", label: "US talent - paywall SKIPPED, submits free",
      href: review("ctry=US"),
      note: "the membership check answers canSubmitFree; no checkout is created" },
    { group: "Step 4 - who pays", label: "Already PRO - no second subscription",
      href: review("mockScenario=alreadyPro") },
    { group: "Step 4 - who pays", label: "Trial already running - straight to Send",
      href: review("mockScenario=trialActive") },
    { group: "Step 4 - who pays", label: "Trial already used - the offer is gone",
      href: review("mockScenario=usedTrial"),
      note: "still reaches the paywall, but with no trial to give" },
    { group: "Step 4 - who pays", label: "Not eligible for a trial",
      href: review("mockScenario=notEligible") },
    { group: "Happy path", label: "Existing account: log in from step 1",
      href: `/submission/flow/step/1?${open}&mockScenario=existingUser`,
      note: "Log in opens the login stand-in, then the continue page and step 2" },
    { group: "Happy path", label: "Existing PRO account: log in from step 1",
      href: `/submission/flow/step/1?${open}&mockScenario=existingPro`,
      note: "no phone step, nothing to pay: step 4 sends" },
    { group: "Happy path", label: "Existing account: already logged in on the classic site",
      href: `/submission/flow/step/1?${open}&mockScenario=classicSession`,
      note: "step 1 offers Continue as Paola; Not you? shows the form; Log out at the bottom of steps 2-4" },
    { group: "Step 4 - who pays", label: "Existing user - step 2",
      href: seed("/submission/flow/step/2", "mockScenario=existingUser&materials=none"),
      note: "older than 24 hours: no film center, no age statement; their headshot and newest resume picked, Change opens their resumes" },
    { group: "Step 4 - who pays", label: "Existing user - step 4",
      href: review("mockScenario=existingUser"),
      note: "no job-alerts box" },
    { group: "Step 4 - who pays", label: "Variant B in the US - the paywall never renders",
      href: seed("/submission/paywall", "step4=b&ctry=US"),
      note: "forwards to review instead of showing a price" },

    { group: "Step 4 - checkout", label: "Pay and come back (webhook lags 2 polls)",
      href: review("step4=a"),
      note: "Continue -> mock Stripe -> Card accepted" },
    { group: "Step 4 - checkout", label: "Card declined -> back to the paywall",
      href: seed("/submission/paywall", "step4=b&mockScenario=cardDeclined"),
      note: "variant B also offers Continue without PRO, which saves it for later" },
    { group: "Step 4 - checkout", label: "Checkout session expired",
      href: seed("/submission/paywall", "step4=b&mockScenario=sessionExpired") },
    { group: "Step 4 - checkout", label: "Abandoned 3DS (cancel on the Stripe page)",
      href: seed("/submission/paywall", "step4=b"),
      note: 'pick "3DS abandoned" in the mock checkout outcome list' },

    { group: "Step 4 - checkout", label: "Payment confirmation never arrives",
      href: review("step4=a&mockScenario=webhookSlow"),
      note: "the return page gives up after eight polls; step 4 then waits for the plan too" },

    { group: "End screens (fixtures)", label: "Every end screen (test A, closed after payment, PRO offers)",
      href: "/submission/mock-outcomes",
      note: "test A's payment-sends screens have no server yet, so they render from fixtures" },
    { group: "Step 4 - end screens", label: "Send fails (server error)",
      href: review("ctry=US&mockScenario=sendFails"),
      note: "US talent sends free, so Send is one click; elsewhere, pay first. The error shows under the card" },
    { group: "Step 4 - end screens", label: "Send refused: casting doesn't take the country",
      href: review("ctry=US&mockScenario=countryRefused"),
      note: "Send gives way to See open casting calls and Go to my dashboard; after a payment in test A, the countryAfterPayment outcome" },

    { group: "Step 4 - the card itself", label: 'No note written (renders "(No notes)")',
      href: review("note=none") },
    { group: "Step 4 - the card itself", label: "Save for now -> the hold date",
      href: review("step4=a"),
      note: "Save for now -> /submission/saved?from=review" },
    { group: "Step 4 - the card itself", label: "Expired between step 3 and step 4",
      href: `/submission/mock-seed?to=${encodeURIComponent(
        "/submission/flow/step/4"
      )}&bd=${MOCK_IDS.staleDeadlineBreakdown}&roleid=${MOCK_IDS.staleDeadlineRole}`,
      note: "gates to /submission/closed on mount; no checkout is created" },
    { group: "Step 4 - the card itself", label: "Refresh-safe: land cold on step 4",
      href: review("step4=a"),
      note: "then hit reload - the card must come back, not sit on its skeleton" },

    { group: "Infrastructure", label: "Slow network (3s)",
      href: `${calls}?mockLatency=3000` },
    { group: "Infrastructure", label: "Server error (500)",
      href: `${calls}?mockScenario=error500` },
    { group: "Infrastructure", label: "Rate limited (429)",
      href: `${calls}?mockScenario=rateLimit429` },
    { group: "Infrastructure", label: "Offline / connection failure",
      href: `${calls}?mockScenario=offline` },
    { group: "Infrastructure", label: "No casting calls in this market",
      href: `${calls}?mockScenario=empty` },
  ];
};

const ScenarioLauncher: React.FC<{ search: string }> = ({ search }) => {
  const scenarios = buildScenarios();
  const groups: string[] = [];
  scenarios.forEach((sc) => {
    if (groups.indexOf(sc.group) < 0) groups.push(sc.group);
  });
  return (
    <Launcher>
      <LauncherTitle>
        mock scenarios — <code>{search || "(no query string)"}</code>
      </LauncherTitle>
      {groups.map((group) => (
        <div key={group}>
          <LauncherGroup>{group}</LauncherGroup>
          {scenarios
            .filter((sc) => sc.group === group)
            .map((sc) => (
              <LauncherRow key={sc.href + sc.label}>
                {/* A real anchor, not history.push: several scenarios only take
                    effect on a full load, because the mock overrides are read
                    once and cached in sessionStorage for the tab. */}
                <a href={sc.href}>{sc.label}</a>
                {sc.note ? <LauncherNote> — {sc.note}</LauncherNote> : null}
              </LauncherRow>
            ))}
        </div>
      ))}
      <LauncherNote>
        Reset between runs: <code>sessionStorage.clear()</code> +{" "}
        <code>localStorage.removeItem("CountryCode")</code>
      </LauncherNote>
    </Launcher>
  );
};

const Launcher = styled.div`
  margin: 28px 0 0;
  padding: 12px 14px;
  border: 1px dashed #c9d3e0;
  border-radius: 8px;
  background: #f7f9fc;
  font-size: 12px;
  color: #53627c;
  text-align: left;
`;

const LauncherTitle = styled.div`
  font-weight: 700;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  /* The query string is shown so it can be copied: uppercasing it printed
     BD= and ROLEID=, which are not the parameters the flow reads. */
  code {
    text-transform: none;
    letter-spacing: 0;
  }
`;

const LauncherGroup = styled.div`
  margin: 10px 0 4px;
  font-weight: 700;
  color: #122640;
`;

const LauncherRow = styled.div`
  line-height: 20px;

  a {
    color: #0d6efd;
  }
`;

const LauncherNote = styled.span`
  color: #8a97ab;
`;

export default ScenarioLauncher;
